/**
 * The generation workspace. Owns the flow's state; every child is controlled and stateless.
 *
 * Ported from `app/generate/page.tsx`, the largest page in the app. React Query took the four
 * fetches — rate limit, policy profiles, series list, guardrail config — and every `load()`
 * effect with them; what is left is genuinely about generating an image.
 *
 * ## The rules that actually matter here
 *
 * **1. Flagged text cannot be generated from until it has been acknowledged.** The single most
 * important business rule in the app, so `guardrailBlocking` spells it out rather than being
 * inlined into the button. Read from whichever mode is active — the inactive mode's gate must
 * not block, and must not unblock either. The server re-checks independently; this only stops
 * the honest path.
 *
 * **2. Each acknowledgement is stored as *the text that was acknowledged*, not a boolean.**
 * Editing after ticking therefore invalidates it in the same render — no effect, no stale
 * frame in which Generate is still enabled — and it does so no matter which path changed the
 * text: typing, or "Use this prompt" from either assist panel. Forcing a re-review after every
 * edit is the entire point of the gate. The two modes keep separate acknowledgements: a
 * flagged brief that has been acknowledged says nothing about a flagged edit instruction, and
 * sharing the state would let an acknowledgement leak across inputs nobody reviewed together.
 *
 * **3. Rights and consent is a different gate from the guardrail**, and both must pass. Unlike
 * the acknowledgement these do not reset on every keystroke — they are declarations about the
 * planned shot, not a re-review of exact wording. The edit-mode fields *do* reset when the
 * uploaded file changes, because a confirmation made about one photograph says nothing about
 * a different one.
 *
 * **4. An existing series locks Shape, Lighting, Weather and Visual policy** to its stored
 * values. Display-only here — the route enforces the same lock from the series document, so
 * re-enabling a disabled control in devtools changes nothing.
 *
 * ## Which controls belong to which mode, and why
 *
 * - **Portrait framing is edit-mode only.** It needs a real, already-supplied photograph of a
 *   subject to apply to; describe mode infers its own subject type from the brief.
 * - **Series is describe-mode only.** A locked visual treatment across a family of
 *   *synthesised* scenes has no equivalent when editing one supplied photo.
 * - **Lighting, weather, tier, output, variations, publication context and visual policy apply
 *   to both.** Describe mode feeds lighting and weather into the prompt builder; edit mode
 *   appends the same preset sentence, so turning a supplied summer photo snowy is a button
 *   rather than something the user has to word themselves.
 */

import EditOutlined from "@mui/icons-material/EditOutlined";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";

import { ApiClientError, imageErrorKind } from "@/api/client";
import type { GenerationResult, ImageSeriesWithPreview } from "@/api/queries";
import {
  useGenerateFromDescription,
  useGenerateFromImage,
  useGovernanceProfiles,
  useGuardrailConfig,
  useRateLimit,
  useSeriesList,
} from "@/api/queries";
import { CampaignContextPanel } from "@/components/generate/CampaignContextPanel";
import { CardGroup } from "@/components/generate/CardGroup";
import { ControlGroup, ControlGroupList } from "@/components/generate/ControlGroup";
import { EditForm } from "@/components/generate/EditForm";
import { GuardrailGate, PublicationRiskNote } from "@/components/generate/GuardrailGate";
import { OutputPreview } from "@/components/generate/OutputPreview";
import { PillGroup } from "@/components/generate/PillGroup";
import { PromptCraftPanel } from "@/components/generate/PromptCraftPanel";
import { PageHeading } from "@/components/PageHeading";
import { NotAuthorisedNotice } from "@/components/RequireAuth";
import { Section } from "@/components/Section";
import {
  DEFAULT_PRINT_SIZE,
  DEFAULT_VARIATIONS,
  DESCRIBE_GENERATION_TIERS,
  EDIT_GENERATION_TIERS,
  GENERATION_TIER_HINTS,
  GENERATION_TIER_LABELS,
  LIGHTING_OPTIONS,
  MODE_OPTIONS,
  OUTPUT_TYPE_OPTIONS,
  PORTRAIT_FRAMING_OPTIONS,
  PRINT_SIZE_OPTIONS,
  RATIO_OPTIONS,
  VARIATION_DIRECTION_OPTIONS,
  VARIATION_OPTIONS,
  WEATHER_OPTIONS,
  defaultGenerationTierFor,
  type GenerateMode,
  type GenerationTier,
  type LightingPreference,
  type OutputType,
  type PortraitFraming,
  type PrintSizeKey,
  type PublicationContext,
  type Ratio,
  type Weather,
} from "@/constants/generate";
import { PRINT_SIZE_LABELS, RATIO_ASPECT_CSS } from "@/constants/imageOutput";
import { NETWORK_FAILURE_MESSAGE, SESSION_EXPIRED_MESSAGE } from "@/constants/messages";
import {
  computePublicationRisk,
  detectSensitiveTopics,
  isSyntheticOverrideReasonProvided,
  requiresSyntheticOverrideReason,
} from "@/guardrail/rules";
import {
  isDescribeRightsConsentSatisfied,
  isEditRightsConsentSatisfied,
} from "@/guardrail/rightsConsent";
import { RADIUS_PX, RADIUS_SMALL_PX, SHADOWS, jp } from "@/theme/tokens";

import { PublicationContextSelector, RightsConsentPanel } from "./GeneratePanels";

/** `""` = no series, this = the inline "start one" state, otherwise an existing series id. */
const NEW_SERIES_VALUE = "__new__";

/**
 * The provider's own content policy rejected the image. Worded to make clear this is a
 * *different* gate from the sensitivity guardrail above it — otherwise a user who has already
 * acknowledged that warning reads this as the same check firing twice, with no idea what to
 * change.
 */
const MODERATION_MESSAGE =
  "The image provider's content policy blocked this brief. That's a separate check from " +
  "the sensitive-topic warning above. Acknowledging that one can't override it. Try " +
  "rewording the brief, especially any depiction of real people, graphic content or " +
  "restricted subject matter.";

const GENERIC_FAILURE_MESSAGE =
  "Something went wrong generating this image. Please try again in a moment.";

/**
 * Maps a rejection onto the copy and treatment the user sees.
 *
 * Mode-agnostic on purpose: both generation routes return the identical error shape, so one
 * mapping serves both rather than two that could drift. Four cases, distinguished by status so
 * they cannot be conflated:
 *
 * - **A moderation refusal** gets its own treatment, because the fix — reword — is unlike any
 *   other failure here.
 * - **401** is the token expiring mid-session. The server's own wording is deliberately opaque
 *   protocol vocabulary, so it is replaced with the copy that names the fix.
 * - **Other 4xx** messages — the guardrail rejection, a 429, a missing or oversized image —
 *   are written for the user already and pass through unchanged. 429 is deliberately *not*
 *   special-cased: there are two causes with two different fixes ("wait" and "ask an admin")
 *   and the server already says the right one, so a hardcoded override could only ever be
 *   accurate for one of them.
 * - **5xx** is never echoed. The server collapses unexpected failures to a bare "Internal
 *   server error", which is no use to anybody.
 *
 * A 403 is not here: it is a permanent state with its own card, not an error to retry.
 */
function generationFailure(error: unknown): { moderation: boolean; message: string } {
  if (!(error instanceof ApiClientError)) {
    // The transport only throws `ApiClientError` once it has a response, so anything else is
    // `fetch` itself failing — offline, DNS, CORS, an aborted connection.
    return { moderation: false, message: NETWORK_FAILURE_MESSAGE };
  }
  if (imageErrorKind(error) === "moderation") {
    return { moderation: true, message: MODERATION_MESSAGE };
  }
  if (error.isUnauthenticated) {
    return { moderation: false, message: SESSION_EXPIRED_MESSAGE };
  }
  if (error.status < 500 && error.message) {
    return { moderation: false, message: error.message };
  }
  return { moderation: false, message: GENERIC_FAILURE_MESSAGE };
}

export function GeneratePage() {
  const guardrailConfig = useGuardrailConfig().data;
  const profiles = useGovernanceProfiles().data?.profiles ?? [];
  const seriesList = useSeriesList().data?.series ?? [];
  const rateLimit = useRateLimit().data;

  const describeGeneration = useGenerateFromDescription();
  const editGeneration = useGenerateFromImage();

  const [mode, setMode] = useState<GenerateMode>("describe");
  const [ratio, setRatio] = useState<Ratio>("square");
  const [outputType, setOutputType] = useState<OutputType>("digital");
  const [printSize, setPrintSize] = useState<PrintSizeKey>(DEFAULT_PRINT_SIZE);
  const [portraitFraming, setPortraitFraming] = useState<PortraitFraming>("auto");
  const [lightingPreference, setLightingPreference] = useState<LightingPreference>("auto");
  const [weather, setWeather] = useState<Weather>("auto");
  const [policyProfileId, setPolicyProfileId] = useState("");
  const [seriesId, setSeriesId] = useState("");
  const [newSeriesName, setNewSeriesName] = useState("");
  const [variations, setVariations] = useState(DEFAULT_VARIATIONS);
  const [variationDirection, setVariationDirection] = useState<"explore" | "refine">("refine");
  const [intendedPublicationContexts, setIntendedPublicationContexts] = useState<
    PublicationContext[]
  >(["unspecified"]);
  const [generationTier, setGenerationTier] = useState<GenerationTier>(
    defaultGenerationTierFor("describe"),
  );
  /*
   * Deliberately *not* invalidated on every prompt edit the way the acknowledgement is: this
   * is a business justification for the kind of image being made, not a tick against one exact
   * string, so a wording tweak should not make somebody retype it.
   */
  const [syntheticOverrideReason, setSyntheticOverrideReason] = useState("");

  const [describeDepictsPeople, setDescribeDepictsPeople] = useState(false);
  const [describeDisclosureAcknowledged, setDescribeDisclosureAcknowledged] = useState(false);
  const [editDepictsPeople, setEditDepictsPeople] = useState(false);
  const [editSourceRightsConfirmed, setEditSourceRightsConfirmed] = useState(false);
  const [editSubjectConsentConfirmed, setEditSubjectConsentConfirmed] = useState(false);

  const [prompt, setPrompt] = useState("");
  const [sourceImage, setSourceImage] = useState<File | null>(null);
  const [editInstruction, setEditInstruction] = useState("");
  const [results, setResults] = useState<GenerationResult[] | null>(null);

  const [acknowledgedPrompt, setAcknowledgedPrompt] = useState<string | null>(null);
  const [acknowledgedInstruction, setAcknowledgedInstruction] = useState<string | null>(null);

  /*
   * `creativeTerritory` is derived rather than stored: "perfect" is not a fourth choice a user
   * makes, it is simply what one variation already is.
   */
  const creativeTerritory = variations > 1 ? variationDirection : "perfect";

  // Pure functions of their text, so memoising is enough to keep them live as the user types.
  const guardrailMatch = useMemo(
    () => detectSensitiveTopics(prompt, guardrailConfig),
    [prompt, guardrailConfig],
  );
  const editGuardrailMatch = useMemo(
    () => detectSensitiveTopics(editInstruction, guardrailConfig),
    [editInstruction, guardrailConfig],
  );

  const activeMatch = mode === "describe" ? guardrailMatch : editGuardrailMatch;

  /** Live, like the matches above — it updates as the brief, context or tier changes. */
  const publicationRiskLevel = useMemo(
    () =>
      computePublicationRisk(
        {
          matchedCategories: activeMatch.matchedCategories,
          intendedPublicationContexts,
          generationTier,
        },
        guardrailConfig?.publicationRisk,
      ),
    [activeMatch, intendedPublicationContexts, generationTier, guardrailConfig],
  );

  const guardrailAcknowledged = acknowledgedPrompt !== null && acknowledgedPrompt === prompt;
  const editGuardrailAcknowledged =
    acknowledgedInstruction !== null && acknowledgedInstruction === editInstruction;

  const activeMutation = mode === "describe" ? describeGeneration : editGeneration;
  const submitting = activeMutation.isPending;

  // Rule 1. See this module's doc comment.
  const guardrailBlocking =
    mode === "describe"
      ? guardrailMatch.flagged &&
        (!guardrailAcknowledged ||
          (requiresSyntheticOverrideReason("describe", guardrailMatch.flagged) &&
            !isSyntheticOverrideReasonProvided(syntheticOverrideReason)))
      : editGuardrailMatch.flagged && !editGuardrailAcknowledged;

  // Rule 3. Read from whichever mode is active, same reasoning as the guardrail.
  const rightsConsentBlocking =
    mode === "describe"
      ? !isDescribeRightsConsentSatisfied({
          depictsIdentifiablePeople: describeDepictsPeople,
          syntheticPeopleDisclosureAcknowledged: describeDisclosureAcknowledged,
        })
      : !isEditRightsConsentSatisfied({
          sourceRightsConfirmed: editSourceRightsConfirmed,
          depictsIdentifiablePeople: editDepictsPeople,
          subjectConsentConfirmed: editSubjectConsentConfirmed,
        });

  const inputsReady =
    mode === "describe"
      ? prompt.trim().length > 0
      : sourceImage !== null && editInstruction.trim().length > 0;

  const canGenerate = inputsReady && !submitting && !guardrailBlocking && !rightsConsentBlocking;

  /**
   * Why Generate is unavailable, in the user's terms — `null` when it isn't.
   *
   * `canGenerate` is four independent conditions ANDed together, and the button expressed all
   * four the same way: greyed out, no explanation. That was survivable while Generate sat
   * directly beneath the gate that was blocking it. It is not now the settings follow the
   * brief and the action bar is pinned to the viewport, because "nothing happens when I press
   * Generate" can have its answer scrolled off screen entirely.
   *
   * Inputs first, deliberately: an empty brief is the overwhelmingly common case, and the
   * consent gates cannot meaningfully be satisfied before there is something to consent about.
   * Only ever one reason is shown — the first thing to go and do, not a list of everything
   * outstanding.
   */
  const blockingReason: string | null = (() => {
    if (submitting) return null;
    if (!inputsReady) {
      if (mode === "describe") return "Write a brief above to continue.";
      if (!sourceImage) return "Upload a source image above to continue.";
      return "Describe what should change above to continue.";
    }
    if (guardrailBlocking) {
      // The evidence-risk reason is a second, separate gate behind the acknowledgement, so
      // naming the acknowledgement once it is already ticked would send the user to a box
      // they have dealt with.
      return mode === "describe" &&
        guardrailAcknowledged &&
        requiresSyntheticOverrideReason("describe", guardrailMatch.flagged)
        ? "Note why a fully AI-generated image is appropriate here to continue."
        : "Acknowledge the authenticity check above to continue.";
    }
    if (rightsConsentBlocking) return "Confirm rights & consent above to continue.";
    return null;
  })();

  // Rule 4.
  const selectedSeries: ImageSeriesWithPreview | undefined =
    seriesId && seriesId !== NEW_SERIES_VALUE
      ? seriesList.find((series) => series.id === seriesId)
      : undefined;
  const seriesLocked = Boolean(selectedSeries);

  function handleModeChange(next: GenerateMode) {
    setMode(next);
    activeMutation.reset();
    // Each mode has its own distinct pair of valid tiers — nothing from the old mode's choice
    // carries any meaning in the new one.
    setGenerationTier(defaultGenerationTierFor(next));
    /*
     * A series is describe-mode only, and its selector only renders there. Left set while
     * switching to edit mode it would keep Shape, Lighting and Visual policy showing the
     * series' locked values and disabled for the rest of the session, with nothing on screen
     * to clear it.
     */
    setSeriesId("");
    setNewSeriesName("");
  }

  /**
   * A confirmation made about one photograph says nothing about a different one, so replacing
   * the file clears every rights declaration made for the previous one — the same "invalidate
   * on the thing that actually changed" principle as the acknowledgement, applied to a file.
   */
  function handleSourceImageChange(file: File | null) {
    setSourceImage(file);
    setEditSourceRightsConfirmed(false);
    setEditDepictsPeople(false);
    setEditSubjectConsentConfirmed(false);
  }

  /**
   * A prompt approved in either assist panel becomes the brief, and that is the whole of the
   * integration — no origin plumbing, no "this came from a report" state, no special case
   * anywhere downstream. One handler for both because the two are not merely similar, they are
   * the same operation: replace the brief with an approved string.
   *
   * Two existing behaviours make that safe rather than lucky. The acknowledgement is stored as
   * the text that was acknowledged, so replacing `prompt` invalidates any previous tick in the
   * same render — a flagged approved prompt arrives with Generate disabled until it is
   * acknowledged against the brief actually being generated from. And `inputsReady` reads
   * `prompt` alone, so approved text enables Generate by exactly the same rule as typed text.
   */
  function handleAssistPromptApproved(approved: string) {
    setMode("describe");
    setPrompt(approved);
    // A failure from a previous attempt has nothing to do with this new brief.
    activeMutation.reset();
  }

  function handleGenerate() {
    if (mode === "describe") {
      const isNewSeries = seriesId === NEW_SERIES_VALUE && newSeriesName.trim().length > 0;

      describeGeneration.mutate(
        {
          prompt: prompt.trim(),
          ratio,
          guardrailAcknowledged,
          outputType,
          ...(outputType === "print" ? { printSize } : {}),
          /*
           * A series' locked settings win server-side regardless of what is sent, so they are
           * omitted here too — there is nothing stale or misleading to read in the request.
           */
          ...(!seriesLocked && lightingPreference !== "auto" ? { lightingPreference } : {}),
          ...(!seriesLocked && weather !== "auto" ? { weather } : {}),
          ...(!seriesLocked && policyProfileId ? { policyProfileId } : {}),
          ...(selectedSeries ? { seriesId: selectedSeries.id } : {}),
          ...(isNewSeries ? { newSeriesName: newSeriesName.trim() } : {}),
          intendedPublicationContexts,
          /*
           * Sent as "" rather than omitted when no reason is required. The route reads it only
           * when it requires one, and the schema gives the field a default — so an omission
           * and an empty string are the same request, and spelling it out keeps the body one
           * fixed shape rather than one that changes with the guardrail's mood.
           */
          syntheticOverrideReason: requiresSyntheticOverrideReason(
            "describe",
            guardrailMatch.flagged,
          )
            ? syntheticOverrideReason.trim()
            : "",
          depictsIdentifiablePeople: describeDepictsPeople,
          // Meaningless unless people are declared, and false is what the route assumes then.
          syntheticPeopleDisclosureAcknowledged:
            describeDepictsPeople && describeDisclosureAcknowledged,
          generationTier,
          creativeTerritory,
          variations,
        },
        {
          onSuccess: (data) => {
            setResults(data.generations);
            /*
             * A newly started series' id is only known from this response — select it now so
             * the *next* generation continues it automatically, and clear the typed name since
             * it has done its job. For an existing series this is a no-op.
             */
            if (data.seriesId) {
              setSeriesId(data.seriesId);
              setNewSeriesName("");
            }
          },
          onError: () => setResults(null),
        },
      );
      return;
    }

    // Unreachable through the UI — `canGenerate` requires a file — but silently posting a
    // fileless request would be worse than the guard.
    if (!sourceImage) return;

    const formData = new FormData();
    formData.append("image", sourceImage);
    formData.append("instruction", editInstruction.trim());
    formData.append("ratio", ratio);
    // Form values are always strings; the route accepts the exact literal "true" only.
    formData.append("guardrailAcknowledged", String(editGuardrailAcknowledged));
    formData.append("outputType", outputType);
    if (outputType === "print") formData.append("printSize", printSize);
    if (portraitFraming !== "auto") formData.append("subjectType", portraitFraming);
    if (lightingPreference !== "auto") formData.append("lightingPreference", lightingPreference);
    if (weather !== "auto") formData.append("weather", weather);
    if (policyProfileId) formData.append("policyProfileId", policyProfileId);
    // Appended once per value rather than as a joined string — the route reads a repeated key.
    for (const context of intendedPublicationContexts) {
      formData.append("intendedPublicationContexts", context);
    }
    formData.append("sourceRightsConfirmed", String(editSourceRightsConfirmed));
    formData.append("depictsIdentifiablePeople", String(editDepictsPeople));
    formData.append("subjectConsentConfirmed", String(editSubjectConsentConfirmed));
    formData.append("generationTier", generationTier);
    formData.append("creativeTerritory", creativeTerritory);
    formData.append("variations", String(variations));

    editGeneration.mutate(formData, {
      onSuccess: (data) => setResults(data.generations),
      onError: () => setResults(null),
    });
  }

  const notAuthorised =
    activeMutation.error instanceof ApiClientError && activeMutation.error.isNotAuthorised;
  const failure = activeMutation.isError && !notAuthorised
    ? generationFailure(activeMutation.error)
    : null;

  /*
   * Any failure from the last attempt, immediately above the action bar — so a rejection
   * appears next to the control that caused it rather than at whatever scroll position the
   * user happened to submit from.
   */
  const submitFeedback = (
    <>
      {notAuthorised ? <NotAuthorisedNotice /> : null}
      {failure?.moderation ? (
        /*
         * Visually distinct from the one-liner every other failure gets: this is a policy
         * decision the user has to act on, not a glitch to retry. Warning rather than error,
         * matching how the guardrail gate treats this app's own check.
         */
        <Alert severity="warning" variant="outlined" role="alert" icon={<ShieldOutlined />}>
          <AlertTitle>Provider content policy</AlertTitle>
          <Typography variant="body2">{failure.message}</Typography>
        </Alert>
      ) : failure ? (
        <Alert severity="error" role="alert">
          {failure.message}
        </Alert>
      ) : null}
    </>
  );

  /*
   * The action bar.
   *
   * Generate used to live inside whichever input section was showing, which was right while
   * the brief was the last thing on the page. It no longer is: the brief is near the top now
   * and the settings follow it, so an inline button would sit halfway up a long form with
   * controls below it that still change what it does.
   *
   * `position: sticky` with `bottom`, not `fixed`: it pins to the bottom of the viewport for
   * exactly as long as the form is on screen, then scrolls away naturally once the user
   * reaches the results below. Always reachable while there is something to submit, never a
   * permanent strip over a finished image. The form wrapper is what bounds that.
   */
  const actionBar = (
    <Box
      sx={{
        position: "sticky",
        bottom: 16,
        zIndex: 3,
        px: { xs: 2, sm: 2.5 },
        py: 1.5,
        borderRadius: `${RADIUS_PX}px`,
        border: `1px solid ${jp.line}99`,
        backgroundColor: "rgba(255,255,255,0.95)",
        backdropFilter: "blur(16px)",
        boxShadow: SHADOWS.panel,
      }}
    >
      <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Button variant="contained" loading={submitting} disabled={!canGenerate} onClick={handleGenerate}>
          Generate
        </Button>
        {/*
          The status area. While submitting it says so and nothing else; otherwise the
          blocking reason and the quota are *both* shown, stacked.

          Deliberately not one line with the reason taking priority: they answer different
          questions — "why can't I press this" and "how many do I have left" — and the quota
          is most useful precisely while someone is still writing the brief and deciding how
          many variations to ask for. Hiding it behind the blocked state would make it appear
          only at the moment it stops mattering.
        */}
        {submitting ? (
          <Typography variant="caption" sx={{ color: jp.slate }}>
            This usually takes a few seconds.
          </Typography>
        ) : (
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            {/*
              `aria-live="polite"` because a disabled button announces nothing about *why*.
              Without this a screen-reader user has to go hunting for the gate blocking them.
            */}
            {blockingReason ? (
              <Typography
                variant="caption"
                aria-live="polite"
                sx={{ color: jp.warningText, fontWeight: 600 }}
              >
                {blockingReason}
              </Typography>
            ) : null}
            {rateLimit ? (
              <Typography variant="caption" sx={{ color: jp.slate }}>
                {rateLimit.count} of {rateLimit.max} generations used this hour
                {/* Only present once an admin has granted this client a monthly tier. */}
                {rateLimit.monthly
                  ? ` · ${rateLimit.monthly.count} of ${rateLimit.monthly.max} this month`
                  : ""}
              </Typography>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Box>
  );

  const tierOptions = (mode === "edit" ? EDIT_GENERATION_TIERS : DESCRIBE_GENERATION_TIERS).map(
    (tier) => ({ value: tier, label: GENERATION_TIER_LABELS[tier], hint: GENERATION_TIER_HINTS[tier] }),
  );

  return (
    <Stack spacing={4}>
      <PageHeading
        overline="Create an image"
        title="Describe it, or supply an image"
        lead="Pick how you want to start, say what you want, then adjust the settings if you need to. Everything you generate is saved to your history."
      />

      {/*
        Everything that makes up one request, wrapped together so the sticky action bar at the
        end has this — rather than the whole page — as its containing block: the bar stays
        pinned while any part of the form is on screen and then scrolls away once the user
        passes it, instead of hanging over the results below.
      */}
      <Stack spacing={4}>
      {/*
        The primary fork, first and alone in its own Section. It was a pill pair tucked inside
        "Shape", which read as a formatting detail rather than the choice that decides which
        form, which endpoint and which half of the controls below are even shown.
      */}
      <Section
        title="How do you want to start?"
        description="This decides what you fill in below."
      >
        <CardGroup
          label="How to start"
          columns={2}
          stackOnMobile
          value={mode}
          onChange={handleModeChange}
          disabled={submitting}
          options={MODE_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
            detail: option.hint,
            icon:
              option.value === "describe" ? (
                <EditOutlined fontSize="small" />
              ) : (
                <FileUploadOutlined fontSize="small" />
              ),
          }))}
        />
      </Section>

      {mode === "describe" ? (
        <Section title="Your brief">
          <Stack spacing={3}>
            {/* Above the brief because it is a way of *filling in* the brief — somebody who
                already knows what they want scrolls past it. */}
            <CampaignContextPanel
              onPromptApproved={handleAssistPromptApproved}
              disabled={submitting}
              config={guardrailConfig}
            />

            <TextField
              id="describe-prompt"
              label="Your brief"
              multiline
              minRows={6}
              placeholder={DESCRIBE_PLACEHOLDER}
              helperText="The more specific the description, the closer the result. Aim for a sentence or two."
              value={prompt}
              disabled={submitting}
              error={guardrailMatch.flagged}
              onChange={(event) => setPrompt(event.target.value)}
            />

            {/* Directly beneath the textarea, because unlike the panel above it this is an
                action *on* that text rather than another way of filling it in. */}
            <PromptCraftPanel
              prompt={prompt}
              onPromptApproved={handleAssistPromptApproved}
              disabled={submitting}
              config={guardrailConfig}
            />

            <GuardrailGate
              id="guardrail-describe-acknowledgement"
              match={guardrailMatch}
              config={guardrailConfig}
              acknowledged={guardrailAcknowledged}
              onAcknowledgeChange={(value) => setAcknowledgedPrompt(value ? prompt : null)}
              disabled={submitting}
            />

            <PublicationRiskNote
              level={publicationRiskLevel}
              policy={guardrailConfig?.publicationRisk}
            />

            {/*
              The evidence-risk control. This mode produces a fully synthetic image — nothing
              was photographed — so a flagged brief needs a recorded reason in addition to the
              plain acknowledgement above. Enforced independently by the route.
            */}
            {requiresSyntheticOverrideReason("describe", guardrailMatch.flagged) ? (
              <Stack
                spacing={1}
                sx={{
                  p: 2,
                  border: "1px solid rgba(217,119,6,0.4)",
                  backgroundColor: "rgba(217,119,6,0.05)",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  This use may require additional review because a reasonable reader could
                  interpret this image as evidence that something exists, happened or was
                  achieved. Briefly note why a synthetic image is appropriate for this brief —
                  for example, it will be clearly labelled as illustrative, or it depicts a
                  concept rather than a specific real event.
                </Typography>
                {/*
                  A real `label` rather than an `aria-label`: MUI puts an unrecognised prop on
                  the wrapper rather than on the control, so an `aria-label` here would leave
                  the textarea itself unlabelled — invisible to a screen reader, and to any
                  test that looks the field up the way a user would.
                */}
                <TextField
                  id="synthetic-override-reason"
                  label="Why is a fully AI-generated image appropriate here?"
                  multiline
                  minRows={2}
                  value={syntheticOverrideReason}
                  disabled={submitting}
                  onChange={(event) => setSyntheticOverrideReason(event.target.value)}
                />
              </Stack>
            ) : null}

            <RightsConsentPanel
              mode="describe"
              depictsIdentifiablePeople={describeDepictsPeople}
              onDepictsIdentifiablePeopleChange={setDescribeDepictsPeople}
              syntheticPeopleDisclosureAcknowledged={describeDisclosureAcknowledged}
              onSyntheticPeopleDisclosureAcknowledgedChange={setDescribeDisclosureAcknowledged}
              disabled={submitting}
            />
          </Stack>
        </Section>
      ) : (
        <Section
          title="Supply an image"
          description="Upload a source image and describe how it should change."
        >
          <Stack spacing={3}>
            <EditForm
              file={sourceImage}
              onFileChange={handleSourceImageChange}
              instruction={editInstruction}
              onInstructionChange={setEditInstruction}
              disabled={submitting}
              guardrailFlagged={editGuardrailMatch.flagged}
            />

            <RightsConsentPanel
              mode="edit"
              depictsIdentifiablePeople={editDepictsPeople}
              onDepictsIdentifiablePeopleChange={setEditDepictsPeople}
              sourceRightsConfirmed={editSourceRightsConfirmed}
              onSourceRightsConfirmedChange={setEditSourceRightsConfirmed}
              subjectConsentConfirmed={editSubjectConsentConfirmed}
              onSubjectConsentConfirmedChange={setEditSubjectConsentConfirmed}
              disabled={submitting}
            />

            {/* A second, independent gate — its own match and its own acknowledgement, hence
                its own checkbox id. */}
            <GuardrailGate
              id="guardrail-edit-acknowledgement"
              match={editGuardrailMatch}
              config={guardrailConfig}
              acknowledged={editGuardrailAcknowledged}
              onAcknowledgeChange={(value) =>
                setAcknowledgedInstruction(value ? editInstruction : null)
              }
              disabled={submitting}
            />

            <PublicationRiskNote
              level={publicationRiskLevel}
              policy={guardrailConfig?.publicationRisk}
            />
          </Stack>
        </Section>
      )}

        {/*
          Everything from here down is a setting *on* the request above, and all of it has a
          working default — which is why it now follows the brief instead of standing between
          the user and it. Nine top-level Sections became three, each grouping the settings
          that get decided together; see `ControlGroup`. Nothing is hidden or collapsed: this
          is an auditing tool, and a governance control behind a disclosure triangle is a
          governance control nobody reads.
        */}
        <Section
          title="Format"
          description="The shape and size of what comes back, and how many options to produce."
        >
          <ControlGroupList>
            <ControlGroup
              label="Shape"
              description={
                seriesLocked
                  ? "Locked to this series' shape so every image in it matches."
                  : "Applies to both ways of starting."
              }
            >
              <CardGroup
                label="Aspect ratio"
                columns={3}
                value={selectedSeries?.lockedSettings.ratio ?? ratio}
                onChange={setRatio}
                disabled={submitting || seriesLocked}
                options={RATIO_OPTIONS.map((option) => ({
                  value: option.value,
                  label: option.label,
                  detail: option.size,
                  preview: (
                    <Box
                      aria-hidden
                      sx={{
                        height: { xs: 48, sm: 64 },
                        aspectRatio: RATIO_ASPECT_CSS[option.value],
                        // The thumbnail radius, not the card's. These frames exist to show a
                        // *proportion*, and a 24px radius on a box 48px tall ate the corners
                        // entirely — portrait and square became near-indistinguishable blobs,
                        // which is the one job they have.
                        borderRadius: `${RADIUS_SMALL_PX}px`,
                        backgroundColor: jp.mist,
                        border: `1px solid ${jp.line}`,
                      }}
                    />
                  ),
                }))}
              />
            </ControlGroup>

            <ControlGroup
              label="Output"
              description="What this image is for. A print export is upscaled and sized for a physical print."
            >
              <Stack spacing={2.5}>
                <CardGroup
                  label="Output"
                  columns={2}
                  stackOnMobile
                  options={OUTPUT_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label, detail: o.hint }))}
                  value={outputType}
                  onChange={setOutputType}
                  disabled={submitting}
                />
                {/* Only once "Print" is chosen — an irrelevant size picker under the default
                    option would suggest it always matters. */}
                {outputType === "print" ? (
                  <Stack spacing={1}>
                    <PillGroup
                      label="Print size"
                      options={PRINT_SIZE_OPTIONS.map((key) => ({ value: key, label: PRINT_SIZE_LABELS[key] }))}
                      value={printSize}
                      onChange={setPrintSize}
                      disabled={submitting}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Sized to the closest standard print proportions; a shape that doesn&rsquo;t
                      exactly match your chosen aspect ratio crops slightly rather than distorting.
                    </Typography>
                  </Stack>
                ) : null}
              </Stack>
            </ControlGroup>

            <ControlGroup
              label="Variations"
              description="How many options to generate at once from this one brief. Each counts toward your generation limit."
            >
              <Stack spacing={2}>
                <PillGroup
                  label="Number of variations"
                  options={VARIATION_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
                  value={variations}
                  onChange={setVariations}
                  disabled={submitting}
                />
                {variations > 1 ? (
                  <Stack spacing={1}>
                    <Typography variant="overline" color="text.secondary">
                      With more than one, are you exploring or refining?
                    </Typography>
                    <PillGroup
                      label="Variation direction"
                      options={VARIATION_DIRECTION_OPTIONS}
                      value={variationDirection}
                      onChange={setVariationDirection}
                      disabled={submitting}
                    />
                  </Stack>
                ) : null}
              </Stack>
            </ControlGroup>
          </ControlGroupList>
        </Section>

        <Section
          title="Look"
          description="How the image is treated. Every one of these has a sensible default — skip the lot if you have no view."
        >
          <ControlGroupList>
            <ControlGroup
              label="Generation tier"
              description={
                mode === "edit"
                  ? "How far the AI may change your uploaded photo."
                  : "How far the result may depart from strict photorealism."
              }
            >
              <PillGroup
                label="Generation tier"
                options={tierOptions}
                value={generationTier}
                onChange={setGenerationTier}
                disabled={submitting}
              />
            </ControlGroup>

            {/* Edit-mode only: portrait framing needs a real, already-supplied photograph to
                apply to. Describe mode infers its own subject type from the brief. */}
            {mode === "edit" ? (
              <ControlGroup
                label="Portrait framing"
                description="If this is a portrait, choose how tightly to frame it. Leave on Auto otherwise."
              >
                <PillGroup
                  label="Portrait framing"
                  options={PORTRAIT_FRAMING_OPTIONS}
                  value={portraitFraming}
                  onChange={setPortraitFraming}
                  disabled={submitting}
                />
              </ControlGroup>
            ) : null}

            <ControlGroup
              label="Light"
              description="Time of day and light quality. Auto lets the brief or the source photo decide."
            >
              <PillGroup
                label="Lighting"
                options={LIGHTING_OPTIONS}
                value={
                  selectedSeries ? (selectedSeries.lockedSettings.lightingPreference ?? "auto") : lightingPreference
                }
                onChange={setLightingPreference}
                disabled={submitting || seriesLocked}
              />
            </ControlGroup>

            <ControlGroup label="Weather" description="Conditions in the scene.">
              <PillGroup
                label="Weather"
                options={WEATHER_OPTIONS}
                value={selectedSeries ? (selectedSeries.lockedSettings.weather ?? "auto") : weather}
                onChange={setWeather}
                disabled={submitting || seriesLocked}
              />
            </ControlGroup>

            <ControlGroup
              label="Visual policy"
              description="Apply this organisation's saved photographic and governance principles, if any exist. Set up profiles from Governance in the admin area."
            >
              <Stack spacing={1}>
                <TextField
                  select
                  fullWidth
                  label="Visual policy profile"
                  value={selectedSeries ? (selectedSeries.lockedSettings.policyProfileId ?? "") : policyProfileId}
                  disabled={submitting || seriesLocked || profiles.length === 0}
                  onChange={(event) => setPolicyProfileId(event.target.value)}
                >
                  <MenuItem value="">No visual policy</MenuItem>
                  {profiles.map((profile) => (
                    <MenuItem key={profile.id} value={profile.id}>
                      {profile.name}
                    </MenuItem>
                  ))}
                </TextField>
                {profiles.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No visual policy profiles have been created yet.
                  </Typography>
                ) : null}
              </Stack>
            </ControlGroup>

            {/* Describe-mode only: a locked treatment across a family of *synthesised* scenes
                has no equivalent when editing one supplied photo. */}
            {mode === "describe" ? (
              <ControlGroup
                label="Image series"
                description="Lock a visual treatment across a family of images — an annual report's photography, a campaign — rather than generating each one independently."
              >
                <SeriesSelector
                  seriesList={seriesList}
                  value={seriesId}
                  onChange={setSeriesId}
                  newSeriesName={newSeriesName}
                  onNewSeriesNameChange={setNewSeriesName}
                  disabled={submitting}
                />
              </ControlGroup>
            ) : null}
          </ControlGroupList>
        </Section>

        <Section
          title="Governance"
          description="Recorded for audit — the AI image register and the integrity record both show it — but it changes none of the checks above."
        >
          <ControlGroup
            label="Publication context"
            description="Where this image is intended to be used."
          >
            <PublicationContextSelector
              value={intendedPublicationContexts}
              onChange={setIntendedPublicationContexts}
              disabled={submitting}
            />
          </ControlGroup>
        </Section>

        {submitFeedback}
        {actionBar}
      </Stack>

      {results && results.length > 0 ? (
        <Section title={results.length > 1 ? "Results" : "Result"}>
          {/* A batch lays out side by side so they read as options to compare; a single
              result keeps the full-width single-column look. */}
          <Box className={results.length > 1 ? "grid gap-6 sm:grid-cols-2" : undefined}>
            {results.map((item) => (
              <Stack key={item.generationId} spacing={1}>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                  {results.length > 1 ? (
                    <Typography variant="overline" color="text.secondary">
                      Option {item.variationIndex} of {item.variationCount}
                    </Typography>
                  ) : null}
                  {item.seriesDriftWarning ? (
                    <Chip size="small" color="warning" label="Colour differs from the rest of the series" />
                  ) : null}
                  {item.publicationRiskLevel !== "standard" ? (
                    <Chip
                      size="small"
                      color={item.publicationRiskLevel === "high" ? "error" : "warning"}
                      label={
                        guardrailConfig?.publicationRisk.labels[item.publicationRiskLevel] ??
                        item.publicationRiskLevel
                      }
                    />
                  ) : null}
                </Stack>
                <OutputPreview result={item} />
              </Stack>
            ))}
          </Box>
        </Section>
      ) : null}
    </Stack>
  );
}

const DESCRIBE_PLACEHOLDER =
  "Describe the image you want: subject, setting, lighting, mood and framing. " +
  'e.g. "Two colleagues reviewing plans on a factory mezzanine, late-afternoon side light, ' +
  'muted industrial palette, shot on 35mm, editorial documentary feel."';

/**
 * Picks or starts an image series.
 *
 * Once an existing series is selected the page disables Shape, Lighting, Weather and Visual
 * policy and shows that series' locked values — this component owns only the picker and the
 * inline name field, not that override.
 */
function SeriesSelector({
  seriesList,
  value,
  onChange,
  newSeriesName,
  onNewSeriesNameChange,
  disabled,
}: {
  seriesList: ImageSeriesWithPreview[];
  value: string;
  onChange: (value: string) => void;
  newSeriesName: string;
  onNewSeriesNameChange: (name: string) => void;
  disabled: boolean;
}) {
  const selected = seriesList.find((series) => series.id === value);

  return (
    <Stack spacing={2}>
      <TextField
        select
        fullWidth
        label="Image series"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        <MenuItem value="">No series — a one-off image</MenuItem>
        {seriesList.map((series) => (
          <MenuItem key={series.id} value={series.id}>
            {series.name} ({series.generationCount} so far)
          </MenuItem>
        ))}
        <MenuItem value={NEW_SERIES_VALUE}>+ Start a new series…</MenuItem>
      </TextField>

      {value === NEW_SERIES_VALUE ? (
        <TextField
          fullWidth
          label="Series name"
          placeholder='e.g. "Q3 sustainability report"'
          value={newSeriesName}
          disabled={disabled}
          onChange={(event) => onNewSeriesNameChange(event.target.value)}
        />
      ) : null}

      {selected ? (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "center",
            p: 1.5,
            border: `1px solid ${jp.line}`,
            backgroundColor: "rgba(255,255,255,0.6)",
          }}
        >
          {selected.referencePreviewUrl ? (
            <Box
              component="img"
              src={selected.referencePreviewUrl}
              alt={`${selected.name} reference image`}
              sx={{ height: 56, width: 56, flexShrink: 0, borderRadius: `${RADIUS_SMALL_PX}px`, objectFit: "cover" }}
            />
          ) : (
            <Stack
              sx={{
                height: 56,
                width: 56,
                flexShrink: 0,
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                borderRadius: `${RADIUS_SMALL_PX}px`,
                border: `1px dashed ${jp.line}`,
              }}
            >
              <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1.2 }} color="text.secondary">
                First shot in series
              </Typography>
            </Stack>
          )}
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Locked: ratio, lighting, weather and visual policy stay fixed for every image in
              this series.
            </Typography>
            <Box>
              <Chip
                size="small"
                label={`${selected.generationCount} generation${selected.generationCount === 1 ? "" : "s"} so far`}
              />
            </Box>
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
}
